import type {
  ApiBulkRouteLabelGroupPatch,
  ApiBulkRouteLabelPatch,
  ApiCreateRouteLabelGroupPayload,
  ApiCreateRouteLabelPayload,
  ApiRouteLabelConfig,
} from "griptonite-api-client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addGroup,
  addLabel,
  applyGroupUpdates,
  applyLabelUpdates,
  getListEndIndex,
  removeGroup,
  removeLabel,
} from "@/components/RouteLabels/routeLabelsConfig";
import { showRouteLabelsError } from "@/components/RouteLabels/routeLabelsToast";

import api from "../core/api";
import { useCurrentVenueUuid } from "./venues";

const routeLabelsKey = (venueUuid?: string) => ["venues", venueUuid, "route-labels"] as const;

// The config screen and the archive page share one query that includes the
// archived items, so archiving and restoring show up on both without a refetch.
export const useRouteLabelConfigQuery = () => {
  const venueUuid = useCurrentVenueUuid();
  return useQuery({
    queryKey: routeLabelsKey(venueUuid),
    queryFn: () => api.routes.getVenueRouteLabels(venueUuid!, { includeArchived: true }),
    enabled: !!venueUuid,
  });
};

type ConfigMutationOptions<TVariables, TData> = {
  mutationFn: (venueUuid: string, variables: TVariables) => Promise<TData>;
  // Shows the change before the server confirms it. If the save fails, the
  // config goes back to how it was and the error is shown in a toast.
  optimistic?: (config: ApiRouteLabelConfig, variables: TVariables) => ApiRouteLabelConfig;
  // Folds the server's answer into the config, for creates.
  applyResult?: (config: ApiRouteLabelConfig, data: TData) => ApiRouteLabelConfig;
};

const useConfigMutation = <TVariables, TData>({
  mutationFn,
  optimistic,
  applyResult,
}: ConfigMutationOptions<TVariables, TData>) => {
  const venueUuid = useCurrentVenueUuid();
  const queryClient = useQueryClient();
  const queryKey = routeLabelsKey(venueUuid);

  return useMutation({
    mutationFn: (variables: TVariables) => {
      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      return mutationFn(venueUuid, variables);
    },
    onMutate: async (variables) => {
      if (!optimistic) {
        return undefined;
      }
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ApiRouteLabelConfig>(queryKey);
      if (previous) {
        queryClient.setQueryData(queryKey, optimistic(previous, variables));
      }
      return { previous };
    },
    onSuccess: (data) => {
      if (applyResult) {
        queryClient.setQueryData<ApiRouteLabelConfig>(queryKey, (config) =>
          config ? applyResult(config, data) : config,
        );
      }
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      showRouteLabelsError(error);
    },
    // The server is the source of truth for anything we guessed at, like where
    // a restored item lands.
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
};

export const useCreateRouteLabelGroupMutation = () => {
  const queryClient = useQueryClient();
  return useConfigMutation({
    mutationFn: async (venueUuid, data: ApiCreateRouteLabelGroupPayload) => {
      const group = await api.routes.createRouteLabelGroup(venueUuid, data);
      // Groups and root labels share one list, but the server numbers them
      // separately, so move the new group to the end of the list.
      const endIndex = getListEndIndex(
        queryClient.getQueryData<ApiRouteLabelConfig>(routeLabelsKey(venueUuid)),
      );
      if (group.orderIndex >= endIndex) {
        return group;
      }
      return api.routes.updateRouteLabelGroup(venueUuid, group.uuid, { orderIndex: endIndex });
    },
    applyResult: addGroup,
  });
};

export const useCreateRouteLabelMutation = () => {
  const queryClient = useQueryClient();
  return useConfigMutation({
    mutationFn: async (venueUuid, data: ApiCreateRouteLabelPayload) => {
      const label = await api.routes.createRouteLabel(venueUuid, data);
      // Options go at the end of their group, which the server handles. Root
      // labels are in the same list as groups, so they may need moving.
      if (label.group) {
        return label;
      }
      const endIndex = getListEndIndex(
        queryClient.getQueryData<ApiRouteLabelConfig>(routeLabelsKey(venueUuid)),
      );
      if (label.orderIndex >= endIndex) {
        return label;
      }
      return api.routes.updateRouteLabel(venueUuid, label.uuid, { orderIndex: endIndex });
    },
    applyResult: addLabel,
  });
};

export const useUpdateRouteLabelGroupMutation = () =>
  useConfigMutation({
    mutationFn: (venueUuid, { uuid, ...data }: ApiBulkRouteLabelGroupPatch) =>
      api.routes.updateRouteLabelGroup(venueUuid, uuid, data),
    optimistic: (config, update) => applyGroupUpdates(config, [update]),
  });

export const useUpdateRouteLabelMutation = () =>
  useConfigMutation({
    mutationFn: (venueUuid, { uuid, ...data }: ApiBulkRouteLabelPatch) =>
      api.routes.updateRouteLabel(venueUuid, uuid, data),
    optimistic: (config, update) => applyLabelUpdates(config, [update]),
  });

// Only for items with a `routeCount` of 0. If a route got the label since the
// config was loaded, the server refuses, the item comes back and the toast says why.
export const useDeleteRouteLabelGroupMutation = () =>
  useConfigMutation({
    mutationFn: (venueUuid, uuid: string) => api.routes.deleteRouteLabelGroup(venueUuid, uuid),
    optimistic: removeGroup,
  });

export const useDeleteRouteLabelMutation = () =>
  useConfigMutation({
    mutationFn: (venueUuid, uuid: string) => api.routes.deleteRouteLabel(venueUuid, uuid),
    optimistic: removeLabel,
  });

export const useBulkUpdateRouteLabelGroupsMutation = () =>
  useConfigMutation({
    mutationFn: (venueUuid, updates: ApiBulkRouteLabelGroupPatch[]) =>
      api.routes.bulkUpdateRouteLabelGroups(venueUuid, updates),
    optimistic: applyGroupUpdates,
  });

export const useBulkUpdateRouteLabelsMutation = () =>
  useConfigMutation({
    mutationFn: (venueUuid, updates: ApiBulkRouteLabelPatch[]) =>
      api.routes.bulkUpdateRouteLabels(venueUuid, updates),
    optimistic: applyLabelUpdates,
  });
